import { FRAME_VARIANTS, resolveColour } from "odyssey-shared";

/**
 * The interaction prompt's tones while its key is withheld (P2.2-T32; ADR-079's amendment of 2026-10-07, point 5): the
 * design's `greyed` state, the key cap's fill at `vellum-400` and the label greyed. The canvas draws the key cap from
 * the atlas's key frame, so the grey is a multiply tint over that frame.
 *
 * The design sets its greyed label in the disabled text colour, `vellum-500`, on the prompt's own ink plate. The
 * canvas prompt has no plate: its label is the outlined pixel face over whatever ground is there, so what keeps it
 * legible is its ink against its own black outline, which is the same on every ground. `vellum-500` is 3.1 to 1
 * against black; the label is `vellum-400`, the grey of the key cap beside it, 5.5 to 1, above the 4.5 to 1 of body
 * text, and still plainly dimmer than the offered label's `vellum-100` at 17.8 to 1 (`tones.test.ts` holds the three
 * figures). On the lightest ground the black outline carries the word, as it does for every canvas label.
 */

/**
 * The label's ink while the key is withheld: `vellum-400`, as a tint.
 */
export const GREYED_INK = Number.parseInt(resolveColour("vellum-400").slice(1), 16);

/**
 * The multiply tint that turns one colour into another, channel by channel: what a texture painted in `from` is
 * tinted with to draw as `to`. A channel of `from` at 0 stays 0.
 *
 * @param from The colour the texture holds, as `#rrggbb`.
 * @param to The colour wanted, as `#rrggbb`; a channel brighter than `from`'s is left as `from`'s.
 * @returns The tint.
 * @example
 * multiplyTint("#f4ecd6", "#8a826f").toString(16);
 * // "908c84"
 */
export function multiplyTint(from: string, to: string): number {
  let tint = 0;
  for (let channel = 0; channel < 3; channel += 1) {
    const held = Number.parseInt(from.slice(1 + 2 * channel, 3 + 2 * channel), 16);
    const wanted = Number.parseInt(to.slice(1 + 2 * channel, 3 + 2 * channel), 16);
    tint = tint * 256 + (held === 0 ? 0 : Math.min(255, Math.round((255 * wanted) / held)));
  }
  return tint;
}

/**
 * The key cap's tint while the key is withheld: the one that draws the key frame's fill as `vellum-400`. The frame's
 * edges darken by the same measure.
 */
export const GREYED_KEY_TINT = multiplyTint(resolveColour(FRAME_VARIANTS.key.fill), resolveColour("vellum-400"));
